import { ReactElement, useCallback, useEffect, useState } from 'react';
import { HeadTag } from 'shared/components';
import {
  Box,
  // Button,
  // Card,
  // CardHeader,
  Chip,
  CircularProgress,
  debounce,
  Grid,
  IconButton,
  Stack,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from '@mui/material';
import RiskAssessmentsStatus from './components/RiskAssessmentsStatus';
import {
  AppFormikSelectWithKeys,
  AppFormikTextField,
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
  AppSelectWithKeys,
} from 'shared/components/AppComponents';
import { ReactComponent as Search } from 'assets/images/ic_search.svg';
import { Formik } from 'formik';
import EmptyPlaceholder from 'shared/components/EmptyPlaceholder';
import RunRiskAssessment, { ITemplate } from './components/RunRiskAssessment';
import { RiskAssessmentService } from 'services/RiskAssessmentService';
import DateFnsAdapter from '@date-io/date-fns';
import { Link, useNavigate } from 'react-router-dom';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import CompletedAssessmentOverview from './components/CompletedAssessmentOverview';
import { Toast } from 'shared/helpers';
import PageHeader from 'shared/components/PageHeader';
import { ReactComponent as Plus } from 'assets/images/ic_plus.svg';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { IAISystem } from 'modules/ai-inventory/ai-system/components/AISystemRecorder';
import { IDataset } from 'modules/ai-inventory/datasets/records-dataset/Main';
import { useDispatch, useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';
import { PaginationActions } from 'state/pagination/Action';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import useSize from 'shared/components/Hooks';

const dateFns = new DateFnsAdapter();

// const SearchFormikContext = ({ onChange }: any): any => {
//   const { values } = useFormikContext();

//   useEffect(() => {
//     onChange(values);
//   }, [values]);

//   return null;
// };

export interface IAssessment {
  _id: string;
  templateId: string;
  assessmentDate: string;
  completionDate: any;
  templateName: string;
  enitityName: string;
  enitityId: string;
  assessorName: string;
  entityType: 'AI System' | 'Dataset';
  status: string;
  version: string;
  datasetId: string;
  aiInventoryId: string;
  toUserEmail: string;
  toUserName: string;
  requester: { name: string; email: string };
  // templateDetails: ITemplate;
  template: ITemplate[];
  responses: { [key: string]: any };
  assessmentType: string;
  aisystems: IAISystem;
  dataset: IDataset;
}

// const STATUS_OPTIONS = [
//   { label: 'In Progress', value: 'In Progress' },
//   { label: 'Completed', value: 'Completed' },
// ];

// const TYPE_OPTIONS = [
//   { label: 'AI System', value: 'AISystem' },
//   { label: 'Dataset', value: 'Dataset' },
// ];

const RiskAssessment = (): ReactElement => {
  const dispatch = useDispatch();
  const assessmentPagination = useSelector(
    (state: IAppState) => state.paginationState.assessment
  );
  // const [selectedTemplate, setSelectedTemplate] = useState<ITemplate | null>();
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [assessments, setAssessments] = useState<IAssessment[]>([]);
  const [showNewRiskModal, setShowNewRiskModal] = useState(false);
  const [showAssementDetailFor, setShowAssementDetailFor] = useState('');
  const { isMobile, isDesktop } = useSize();
  const navigate = useNavigate();
  const [statusCount, setStatusCount] = useState({
    totalCount: 0,
    completedCount: 0,
    inProgressCount: 0,
    assessmentOnAI: 0,
  });
  const [templates, setTemplates] = useState<any[]>([]);
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  // API Calls
  const loadRiskAssessments = (): void => {
    setIsLoadingRecords(true);

    const queryString = new URLSearchParams({
      offset: String(assessmentPagination.offset),
      limit: String(assessmentPagination.pageLimit),
      enitityName: assessmentPagination.search,
      enitityType: assessmentPagination.enitityType,
      status: assessmentPagination.status,
      templateId: assessmentPagination.templateId,
    }).toString();
    RiskAssessmentService.getExternalAssessments(queryString)
      .then((response) => {
        const { assessment, pagination } = response.data.data;

        const {
          assessment: records,
          totalCount,
          completedCount,
          assessmentOnAI,
          inProgressCount,
        } = assessment;
        setStatusCount({
          totalCount,
          completedCount,
          assessmentOnAI,
          inProgressCount,
        });
        setAssessments(records);

        dispatch(
          PaginationActions.setPagination({
            assessment: {
              offset: pagination.offset,
              pageLimit: pagination.limit,
              totalRecords: pagination.total,
              totalPages: pagination.totalPages,
            },
          })
        );
      })
      .catch(() => {})
      .finally(() => {
        setIsLoadingRecords(false);
      });
  };

  const loadAssessmentTemplates = (): void => {
    RiskAssessmentService.getAssessmentTemplates().then((response) => {
      const { templates } = response.data.data;
      /* eslint no-underscore-dangle: 0 */
      // setTemplates(templates);
      // setTemplates(templates.map((d: any) => ({ ...d, id: d._id })));

      const mapped = (templates || []).map((t: any) => ({
        id: t._id,
        templateName: t.templateName,
        scope: t.scope,
        category: t.category || 'General',
        description: t.description,
        version: t.version,
        sections: t.noOfSections ?? 0,
        questionCount: t.noOfQuestions ?? 0,
        estMinutes: t.estimateToComplete ?? undefined,
        covers: t.complianceMapping,
        tags: t.complianceMapping,
      }));
      setTemplates(mapped);
    });
  };

  // UI Actions
  const actionContinueAssessment = (assessment: IAssessment): void => {
    RiskAssessmentService.getAssessmentDetail(assessment._id)
      .then((response) => {
        const { data } = response.data;
        if (data.length > 0) {
          const detail = data[0];
          navigate(`/risk-assessments/${detail._id}/continue`, {
            state: {
              assessment: {
                datasets:
                  detail.entityType === 'Dataset'
                    ? [
                        {
                          _id: detail.datasetId,
                          title: detail.datasetId,
                        },
                      ]
                    : [],
                aiSystems:
                  detail.entityType === 'AI System'
                    ? [
                        {
                          _id: detail.aiInventoryId,
                          title: detail.aiInventoryId,
                        },
                      ]
                    : [],
                assessmentIds: [assessment],
                template: detail.template[0],
                userResponses: detail.responses || {},
              },
            },
          });
        }
      })
      .catch((error: any) => {
        Toast.showInfoMessage(error.message);
      });
  };

  const debouncSearch = useCallback(
    debounce((searchValue: any) => {
      dispatch(
        PaginationActions.setPagination({
          assessment: {
            ...assessmentPagination,
            offset: 1,
            search: searchValue,
          },
        })
      );
    }, 500),
    [assessmentPagination]
  );

  const handlePageChange = (_event: unknown, page: number): void => {
    dispatch(
      PaginationActions.setPagination({
        assessment: {
          offset: page + 1,
        },
      })
    );
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const pageLimit = Number(event.target.value);

    dispatch(
      PaginationActions.setPagination({
        assessment: {
          offset: 1,
          pageLimit,
        },
      })
    );
  };

  const handleTypeChange = (typeValue: string): void => {
    dispatch(
      PaginationActions.setPagination({
        assessment: {
          ...assessmentPagination,
          offset: 1,
          enitityType: typeValue,
        },
      })
    );
  };

  const handleStatusChange = (statusValue: string): void => {
    dispatch(
      PaginationActions.setPagination({
        assessment: {
          ...assessmentPagination,
          offset: 1,
          status: statusValue,
        },
      })
    );
  };

  const handleTemplateChange = (templateValue: string): void => {
    dispatch(
      PaginationActions.setPagination({
        assessment: {
          ...assessmentPagination,
          offset: 1,
          templateId: templateValue,
        },
      })
    );
  };

  const handleClearFilter = (): void => {
    // setSelectedTemplate(null);
    dispatch(
      PaginationActions.setPagination({
        assessment: {
          offset: 1,
          search: '',
          status: '',
          templateId: '',
          enitityType: '',
        },
      })
    );
  };

  // console.log('selectedTemplate', selectedTemplate);
  // Hooks
  useEffect(() => {
    loadRiskAssessments();
    loadAssessmentTemplates();
  }, [
    assessmentPagination.offset,
    assessmentPagination.pageLimit,
    assessmentPagination.search,
    assessmentPagination.status,
    assessmentPagination.enitityType,
    assessmentPagination.templateId,
  ]);

  // UI Elements
  return (
    <>
      <PageHeader
        title="Risk Assessments"
        description="Manage and track risk assessments across all AI systems and datasets"
        actions={
          (orgACLConfig.riskManagement?.includes(
            'riskManagement.riskAssessments.runNewAssessment'
          ) && (
            <AppPrimaryButton
              className="btn-small"
              onClick={() => setShowNewRiskModal(true)}
              startIcon={
                <SvgIcon
                  component={Plus}
                  inheritViewBox
                  className="view-icon"
                  fontSize="inherit"
                />
              }
              title="Run New Assessment"
            />
          )) || <></>
        }
      />

      <Box className="risk-management risk-assessment">
        <HeadTag title="Risk Assessments" />

        <Box className="risk-assessments-wrapper">
          <RiskAssessmentsStatus
            totalAssessments={statusCount.totalCount}
            completedStatus={statusCount.completedCount}
            inProgressStatus={statusCount.inProgressCount}
            assessmentsOnAIStatus={statusCount.assessmentOnAI}
          />

          <Formik
            initialValues={{
              'search-assessment': assessmentPagination.search,
              status: assessmentPagination.status,
              types: assessmentPagination.enitityType,
              templates: assessmentPagination.templateId,
              // 'search-assessment': '',
              // status: '',
              // types: '',
              // templates: '',
            }}
            enableReinitialize
            onSubmit={() => {
              console.log('search');
            }}
          >
            {({ resetForm, values }) => (
              <>
                {console.log('values', values)}
                {/* <SearchFormikContext onChange={debouncSearch} /> */}
                <Stack
                  className="risk-management-nav-wrapper my-2"
                  direction={isMobile ? 'column' : 'row'}
                >
                  <Box
                    width={isMobile ? '100%' : '40%'}
                    className="risk-management-search"
                  >
                    <AppFormikTextField
                      inputProps={{
                        startAdornment: (
                          <SvgIcon
                            component={Search}
                            inheritViewBox
                            className="view-icon"
                            fontSize="inherit"
                            htmlColor="#76A81F"
                          />
                        ),
                      }}
                      outerLabel={false}
                      placeholder="Search Assessments.."
                      name="search-assessment"
                      onChange={(e: any) => {
                        debouncSearch(e.target.value);
                      }}
                    />
                  </Box>
                  <Grid container className="risk-assessment-nav">
                    <Grid item sm={3} xs={12}>
                      <AppFormikSelectWithKeys
                        name="status"
                        className="rework-selection"
                        notched={false}
                        labelId="fix-selection-label"
                        testid="solicitor-dropdown"
                        MenuProps={{
                          className: 'menu-fix-selection single-fix-rework',
                        }}
                        outerLabel
                        placeholder="Filter Status"
                        notEmphasized
                        notSelectablePlaceholder={false}
                        label=""
                        titleKey="title"
                        valueKey="value"
                        items={[
                          {
                            title: 'All Statuses',
                            value: '',
                          },
                          { title: 'In Progress', value: 'In Progress' },
                          { title: 'Completed', value: 'Completed' },
                        ]}
                        onChange={(e: any) =>
                          handleStatusChange(e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item sm={3} xs={12}>
                      <AppFormikSelectWithKeys
                        name="types"
                        className="rework-selection"
                        notched={false}
                        labelId="fix-selection-label"
                        testid="solicitor-dropdown"
                        MenuProps={{
                          className: 'menu-fix-selection single-fix-rework',
                        }}
                        outerLabel
                        placeholder="Filter Type"
                        notEmphasized
                        notSelectablePlaceholder={false}
                        label=""
                        titleKey="title"
                        valueKey="value"
                        items={[
                          { title: 'All Types', value: '' },
                          { title: 'AI System', value: 'AISystem' },
                          { title: 'Dataset', value: 'Dataset' },
                        ]}
                        onChange={(e: any) => handleTypeChange(e.target.value)}
                      />
                    </Grid>
                    <Grid item sm={3} xs={12} className="mt-1">
                      <AppSelectWithKeys
                        items={templates.map((t) => ({
                          title: `${t.templateName} - ${t.scope}`,
                          id: t.id,
                        }))}
                        titleKey="title"
                        valueKey="id"
                        value={assessmentPagination.templateId}
                        placeholder="Select Template"
                        outerLabel
                        // value={selectedTemplate?.id}
                        onChange={(event: any) => {
                          // const templateId = event.target.value;
                          // const template = templates.find(
                          //   (t) => t.id === templateId
                          // );

                          // setSelectedTemplate(template);
                          handleTemplateChange(event.target.value);
                        }}
                      />
                    </Grid>
                    <Grid item sm={3} xs={12}>
                      <AppPrimaryButtonOutlined
                        fullWidth
                        title={isDesktop ? 'Clear Filters' : 'Clear'}
                        onClick={() => {
                          resetForm();
                          handleClearFilter();
                        }}
                      />
                    </Grid>
                  </Grid>
                </Stack>
                {isLoadingRecords && (
                  <Box className="empty-content">
                    <CircularProgress />
                  </Box>
                )}
                {!isLoadingRecords && (
                  <TableContainer>
                    <Table
                      className="app-table"
                      sx={{ minWidth: 650 }}
                      aria-label="simple table"
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell>Entity</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Template</TableCell>
                          <TableCell>Assessor</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Assessment date</TableCell>
                          <TableCell>Completion date</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {assessments.length > 0 ? (
                          assessments.map((row: any) => (
                            <TableRow
                              key={row._id}
                              sx={{
                                '& .MuiTableCell-root': {
                                  whiteSpace: 'nowrap',
                                },
                                '&:last-child td, &:last-child th': {
                                  border: 0,
                                },
                              }}
                            >
                              <TableCell
                                component="th"
                                scope="row"
                                className="word-break"
                              >
                                {orgACLConfig.riskManagement?.includes(
                                  'riskManagement.riskAssessments.view'
                                ) && (
                                  <Link
                                    to={
                                      row.entityType === 'AI System'
                                        ? `/ai-systems/records/${row.enitityId}`
                                        : `/datasets/records/${row.enitityId}`
                                    }
                                  >
                                    {row.enitityName}
                                  </Link>
                                )}
                                {!orgACLConfig.riskManagement?.includes(
                                  'riskManagement.riskAssessments.view'
                                ) && row.enitityName}
                              </TableCell>
                              <TableCell>{row.entityType}</TableCell>
                              <TableCell>{row.templateName}</TableCell>
                              <TableCell className="word-break">
                                {row.assessorName}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={row.status}
                                  size="small"
                                  color={
                                    row.status === 'Completed'
                                      ? 'primary'
                                      : 'default'
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                {dateFns
                                  .date(row.assessmentDate)
                                  .toDateString()}
                              </TableCell>
                              <TableCell>
                                {row.completionDate
                                  ? dateFns
                                      .date(row.completionDate)
                                      .toDateString()
                                  : '-'}
                              </TableCell>
                              <TableCell>
                                <Box
                                  style={{
                                    display: 'flex',
                                    columnGap: '10px',
                                  }}
                                >
                                  <Tooltip title="View">
                                    <IconButton
                                      color="primary"
                                      onClick={() => {
                                        setShowAssementDetailFor(row._id);
                                      }}
                                    >
                                      <RemoveRedEyeIcon />
                                    </IconButton>
                                  </Tooltip>
                                  {row.status === 'In Progress' &&
                                    orgACLConfig.riskManagement?.includes(
                                      'riskManagement.riskAssessments.continue'
                                    ) && (
                                      <Tooltip title="Continue">
                                        <IconButton
                                          onClick={() =>
                                            actionContinueAssessment(row)
                                          }
                                        >
                                          <PlayCircleOutlineIcon />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              <EmptyPlaceholder title="No assessments found matching your criteria." />
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </Formik>
        </Box>
        {showNewRiskModal && (
          <RunRiskAssessment
            open={showNewRiskModal}
            onCloseDialog={() => {
              setShowNewRiskModal(false);
            }}
          />
        )}
        {showAssementDetailFor && (
          <CompletedAssessmentOverview
            open={!!showAssementDetailFor}
            assessmentId={showAssementDetailFor}
            onCloseDialog={() => {
              setShowAssementDetailFor('');
            }}
          />
        )}

        {!isLoadingRecords && assessments.length > 0 && (
          <TablePagination
            component="div"
            count={assessmentPagination.totalRecords}
            page={assessmentPagination.offset - 1}
            rowsPerPage={assessmentPagination.pageLimit}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 20, 50]}
            disabled={isLoadingRecords}
          />
        )}
      </Box>
    </>
  );
};

export default RiskAssessment;
